// Fotos de las cámaras en el tamaño justo. Las originales son 4K (3840×2160, 1-3 MB): bajarlas enteras para cada
// miniatura, recorte y foto principal hacía que cambiar de día en Inteligencia tardara segundos. El backend arma
// miniaturas de 320, 640 y 1280 px una sola vez (GET/POST /api/fotos/mini, caché en S3) y acá se elige la que alcanza:
//   - 320: miniaturas y recortes chicos (y primer paso borroso mientras llega la grande)
//   - 640: recortes con mucho zoom en contenedores chicos
//   - 1280: foto principal
//   - original 4K: solo en el Visor cuando se acerca (primero se ve la de 1280 y la 4K se cambia en segundo plano)
// Si el endpoint no está (backend viejo) o falla, se usa la original de siempre: nunca queda sin foto.
// Para apagarlo: REACT_APP_MINIATURAS=0. En local se apunta a otro backend con REACT_APP_FOTOS_API_URL.
import { getSignedImageUrl } from '../../api/metricsia.api';

const BASE = (process.env.REACT_APP_FOTOS_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081').replace(/\/$/, '');
const ACTIVAS = process.env.REACT_APP_MINIATURAS !== '0';
export const ANCHOS = [320, 640, 1280];
export const ORIGINAL = Infinity;
const TTL = 12 * 60e3; // las URL firmadas viven 15 min (original) / 30-60 min (miniatura)
const LOTE = 60;

const LOCAL = typeof window !== 'undefined' && /^(localhost|127\.\d+\.\d+\.\d+)$/.test(window.location.hostname);
// En local las fotos de S3 pasan por el proxy /s3img (el bucket no deja leer píxeles desde localhost)
const proxy = (u) => (LOCAL && /^https:\/\/[a-z0-9.-]+\.amazonaws\.com\//.test(u) ? '/s3img/' + u.replace(/^https:\/\//, '') : u);
// Solo local, para medir: ?sinCacheFotos=1 en la URL de la página hace que el navegador no reuse miniaturas ya bajadas
const DEV_V = process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('sinCacheFotos') ? Date.now() : null;
const sinCache = (u) => (DEV_V && LOCAL && u.includes('/api/fotos/mini/archivo/') ? `${u}?v=${DEV_V}` : u);
const token = () => { try { return localStorage.getItem('authLisualToken'); } catch (e) { return null; } };

let caidas = !ACTIVAS; // el backend no tiene miniaturas: originales por el resto de la sesión
const original = (s3) => getSignedImageUrl(s3).then(proxy);
/** true si el backend no tiene miniaturas (o están apagadas): conviene usar directo la URL original que ya se tiene. */
export const miniaturasCaidas = () => caidas;

/** Ancho de miniatura para mostrar `px` píxeles reales (ORIGINAL si no alcanza ni la de 1280). */
export const anchoPara = (px) => ANCHOS.find((a) => a >= px) || ORIGINAL;

/**
 * Qué foto pedir para un contenedor de `cssW` px que muestra `photo` (o el recorte `crop` de ella).
 * alta = se permite la 4K (Visor acercado); si no, el tope es 1280 (640 en contenedores chicos).
 */
export function anchoObjetivo(cssW, photo, crop, { alta = false } = {}) {
  if (!cssW || !photo) return 320;
  const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
  const zoom = crop ? Math.max(1, (photo.w || 1920) / Math.max(1, crop.x2 - crop.x1)) : 1;
  const px = cssW * dpr * zoom;
  if (alta) return anchoPara(px);
  const tope = cssW <= 320 ? 640 : 1280; // miniaturas y recortes de la grilla: con 640 alcanza (el Visor muestra la grande)
  return Math.min(tope, anchoPara(px));
}

// --- Pedidos de miniaturas en lote: todas las que se piden en el mismo momento van en un solo POST ---
// Tres colas por ancho: 'alta' (foto principal, sale ya), 'normal' (lo que está en pantalla) y 'baja' (precarga: el
// backend la genera después de lo que alguien está mirando).
const colas = new Map(); // `${w}|${prioridad}` -> { w, prioridad, keys: Map<s3, [ok, mal][]>, timer }
async function vaciar(id) {
  const c = colas.get(id);
  if (!c) return;
  colas.delete(id);
  const { w, prioridad } = c;
  clearTimeout(c.timer);
  const keys = [...c.keys.keys()];
  let urls = null;
  try {
    const t = token();
    // Con tope: si el backend se cuelga armando miniaturas, a los 8 s (20 s la precarga) se usa la original
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const corte = ctl && setTimeout(() => ctl.abort(), prioridad === 'baja' ? 20000 : 8000);
    const r = await fetch(`${BASE}/api/fotos/mini/lote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
      body: JSON.stringify({ w, keys, ...(prioridad !== 'normal' ? { prioridad } : {}) }),
      ...(ctl ? { signal: ctl.signal } : {}),
    }).finally(() => clearTimeout(corte));
    if (r.ok) urls = (await r.json()).urls || {};
    else if ([404, 405, 501, 503].includes(r.status)) caidas = true;
  } catch (e) { /* sin red o sin CORS: se usa la original */ }
  for (const k of keys) {
    const u = urls && urls[k];
    for (const [ok, mal] of c.keys.get(k)) (u ? Promise.resolve(sinCache(proxy(u))) : original(k)).then(ok, mal);
  }
}
function pedirMini(s3, w, prioridad = 'normal') {
  return new Promise((ok, mal) => {
    const id = `${w}|${prioridad}`;
    let c = colas.get(id);
    if (!c) { c = { w, prioridad, keys: new Map(), timer: null }; colas.set(id, c); }
    if (!c.keys.has(s3)) c.keys.set(s3, []);
    c.keys.get(s3).push([ok, mal]);
    if (c.keys.size >= LOTE) vaciar(id);
    else if (!c.timer) c.timer = setTimeout(() => vaciar(id), prioridad === 'alta' ? 0 : prioridad === 'baja' ? 30 : 10);
  });
}

// --- URL firmada por foto y ancho (caché en memoria) ---
const urls = new Map(); // `${s3}|${w}` -> { p, hasta, u }
/**
 * URL de la foto. Sin `w` = la original (como siempre). Con `w` = la miniatura más chica que tenga al menos
 * esos píxeles de ancho (o la original si pide más de 1280).
 */
export function photoUrl(photo, { w, prioridad = 'normal' } = {}) {
  const ancho = w && !caidas ? anchoPara(w) : ORIGINAL;
  const k = `${photo.s3}|${ancho}`;
  const hit = urls.get(k);
  // Si estaba pedida como precarga y todavía no llegó, se vuelve a pedir con prioridad (el backend la pasa adelante)
  if (hit && hit.hasta > Date.now() && !(hit.baja && !hit.u && prioridad !== 'baja')) return hit.p;
  const x = { hasta: Date.now() + TTL, u: null, baja: prioridad === 'baja' };
  x.p = (ancho === ORIGINAL ? original(photo.s3) : pedirMini(photo.s3, ancho, prioridad))
    .then((u) => { x.u = u; return u; })
    .catch((e) => { urls.delete(k); throw e; });
  urls.set(k, x);
  if (urls.size > 3000) urls.delete(urls.keys().next().value);
  return x.p;
}
/** La URL si ya está resuelta (sin esperar), si no null. */
/** Olvida la URL guardada de esa foto y ancho (por ejemplo si la imagen no cargó: la próxima vez se vuelve a pedir). */
export function olvidarUrl(photo, w) { urls.delete(`${photo.s3}|${w && !caidas ? anchoPara(w) : ORIGINAL}`); }
export function photoUrlLista(photo, w) {
  const x = urls.get(`${photo.s3}|${w && !caidas ? anchoPara(w) : ORIGINAL}`);
  return x && x.u && x.hasta > Date.now() ? x.u : null;
}

// --- Imágenes ya bajadas y decodificadas (se muestran al instante al volver a un día) ---
const decod = new Map(); // url -> Promise<HTMLImageElement>
const listas = new Set();
export function cargarImagen(url, { baja = false, alta = false } = {}) {
  if (decod.has(url)) { const v = decod.get(url); decod.delete(url); decod.set(url, v); return v; }
  const im = new Image();
  im.decoding = 'async';
  if (baja) im.fetchPriority = 'low';
  else if (alta) im.fetchPriority = 'high';
  const p = new Promise((ok, mal) => {
    im.onload = () => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()).then(() => { listas.add(url); ok(im); });
    im.onerror = () => { decod.delete(url); mal(new Error('no cargó la foto')); };
  });
  im.src = url;
  decod.set(url, p);
  if (decod.size > 400) { const v = decod.keys().next().value; decod.delete(v); listas.delete(v); }
  return p;
}
export const imagenLista = (url) => !!url && listas.has(url);

// --- Precarga con prioridad baja (día anterior y siguiente, fotos vecinas de la barra) ---
const pendientes = [];
let activas = 0;
const MAX_PRECARGA = 3;
function siguiente() {
  while (activas < MAX_PRECARGA && pendientes.length) {
    const [photo, w] = pendientes.shift();
    activas += 1;
    photoUrl(photo, { w, prioridad: 'baja' }).then((u) => cargarImagen(u, { baja: true })).catch(() => {}).finally(() => { activas -= 1; siguiente(); });
  }
}
/** Pone en cola (al final) fotos para tener listas: [[photo, w], ...]. Las repetidas o ya listas no se piden. */
export function precargar(lista) {
  for (const [photo, w] of lista) {
    if (!photo) continue;
    const u = photoUrlLista(photo, w);
    if (u && imagenLista(u)) continue;
    if (pendientes.some(([p, x]) => p.s3 === photo.s3 && x === w)) continue;
    pendientes.push([photo, w]);
  }
  if (pendientes.length > 200) pendientes.splice(0, pendientes.length - 200);
  // Después de lo que se está mostrando ahora
  setTimeout(siguiente, 400);
}
/** Vacía la cola de precarga (por ejemplo al cambiar de obra). */
export const cancelarPrecarga = () => { pendientes.length = 0; };
